// Libs
import classNames from 'classnames/bind';
import { Avatar, Button, Chip, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { Loading, UnderDevelopment } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { getEmployeeDetail } from '~/thunks/employee/employeeThunk';
import { IEmployeeDetail } from '~/utils/interface/employee';
import { createNewConversation } from '~/thunks/conversation/conversationThunk';
import { IQueryCreateNewConversation } from '~/utils/interface/conversation';
import { DEFAULT_NUMBER_ONE, DEFAULT_NUMBER_ZERO, EMPTY_STRING, ROLES } from '~/utils/constants/common';
import { formatPhoneNumber, getFullAddress, getFullName } from '~/utils/helper/common';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './EmployeeDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const EmployeeDetailPage = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/employee', 'common/base']);
  const { id: employeeId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [employeeDetail, setEmployeeDetail] = useState<IEmployeeDetail>();
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentRole] = useState<string>(localStorage.getItem(StorageEnum.ROLE) || EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!employeeId) return;

    handleGetEmployeeDetail(employeeId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetEmployeeDetail = (id: string) => {
    setIsLoading(true);

    dispatch(getEmployeeDetail(id))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setEmployeeDetail(res?.data);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const roleName = (userRole: string) => {
    const role = ROLES.find((role) => role.id === userRole);

    return role?.name || EMPTY_STRING;
  };

  const handleBackToEmployees = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.employees);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.employees);
        break;
    }
  };

  const handleEdit = () => {
    if (!employeeId) return;

    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.updateEmployee(employeeId));
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.updateEmployee(employeeId));
        break;
    }
  };

  const handleMessage = () => {  
    if (!employeeId) return;

    const payload: IQueryCreateNewConversation = {
      accountIds: [Number(employeeId)],
    };

    setIsLoading(true);

    dispatch(createNewConversation({ accountIds: payload?.accountIds }))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { id } = res.data;

        if (id) {
          const params = new URLSearchParams({ conversationId: id });

          switch (currentRole) {
            case RoleEnum.ADMIN:
              navigate(`${adminRouteAbsolute.message}?${params.toString()}`);
              break;

            case RoleEnum.STAFF:
              navigate(`${staffRouteAbsolute.message}?${params.toString()}`);
              break;
              
            default:
              break;
          }
        }
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseUnderDevelopment = () => {
    setIsShowUnderDevelopment(false);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='employeeDetailPage'
      title={t('employee_detail_title')}
      onBackToPreviousPage={handleBackToEmployees}
    >
      <>
        <Stack direction={'column'} gap={'20px'} className={cx('container')}>
        <Stack direction={'row'} alignItems={'flex-end'} gap={'12px'} className={cx('avatarGroup')}>
          <Avatar src={employeeDetail?.avatarImage} className={cx('avatar')}>
            {getFullName({ ...employeeDetail }) &&
              `${getFullName({ ...employeeDetail }).split(' ')[DEFAULT_NUMBER_ZERO][DEFAULT_NUMBER_ZERO]}${
                getFullName({ ...employeeDetail }).split(' ')[DEFAULT_NUMBER_ONE][DEFAULT_NUMBER_ZERO]
              }`}
          </Avatar>

          <Stack direction={'column'} gap={'8px'}>
            <Typography className={cx('userName')}>{getFullName({ ...employeeDetail })}</Typography>

            <Chip
              label={employeeDetail?.role ? roleName(employeeDetail?.role) : EMPTY_STRING}
              size='small'
              className={cx('role')}
            />
          </Stack>
        </Stack>

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          gap={'20px'}
          className={cx('actionGroup')}
        >
          <Button
            size='large'
            variant='contained'
            fullWidth
            startIcon={
              <img
                className={cx('btnIcon')}
                src={icons.employeeIconEditInfo}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            }
            onClick={handleEdit}
          >
            {t('employee_detail_btn_edit_info')}
          </Button>

          <Button
            size='large'
            variant='outlined'
            fullWidth
            startIcon={
              <img
                className={cx('btnIcon')}
                src={icons.employeeIconMessage}
                alt={t('common_alt_img', { ns: 'common/base' })}
              />
            }
            onClick={handleMessage}
          >
            {t('employee_detail_btn_message')}
          </Button>
        </Stack>

        <Stack direction={'column'} gap={'20px'} className={cx('group')}>
          <Typography component={'h5'} className={cx('title')}>
            {t('employee_detail_contact_information')}
          </Typography>

          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
            <Typography component={'p'} className={cx('key')}>
              {t('employee_detail_phone_number')}
            </Typography>
            <Typography component={'p'} className={cx('value')}>
              {employeeDetail?.phoneNumber ? formatPhoneNumber(employeeDetail?.phoneNumber) : EMPTY_STRING}
            </Typography>
          </Stack>

          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
            <Typography component={'p'} className={cx('key')}>
              {t('employee_detail_email')}
            </Typography>
            <Typography component={'p'} className={cx('value')}>
              {employeeDetail?.email || EMPTY_STRING}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction={'column'} gap={'20px'} className={cx('group')}>
          <Typography component={'h5'} className={cx('title')}>
            {t('employee_detail_work_information')}
          </Typography>

          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
            <Typography component={'p'} className={cx('key')}>
              {t('employee_detail_employee_id')}
            </Typography>
            <Typography component={'p'} className={cx('value')}>
              {employeeDetail?.id || EMPTY_STRING}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction={'column'} gap={'20px'} className={cx('group')}>
          <Typography component={'h5'} className={cx('title')}>
            {t('employee_detail_personal_information')}
          </Typography>

          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
            <Typography component={'p'} className={cx('key')}>
              {t('employee_detail_name')}
            </Typography>
            <Typography component={'p'} className={cx('value')}>
              {getFullName({ ...employeeDetail })}
            </Typography>
          </Stack>

          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={'24px'}>
            <Typography component={'p'} className={cx('key')}>
              {t('employee_detail_address')}
            </Typography>
            <Typography component={'p'} className={cx('value')}>
              {getFullAddress({ ...employeeDetail })}
            </Typography>
          </Stack>
        </Stack>
        </Stack>

        {isLoading && <Loading />}

        <UnderDevelopment isOpen={isShowUnderDevelopment} onClose={handleCloseUnderDevelopment} />
      </>
    </ChildrenPage>
  );
};

export default EmployeeDetailPage;
